/**
 * Ecosystem detail page — reusable for HX Energy, Automotive, Systems, Infrastructure, Academy.
 * Route: /:slug (bound via App.js).
 */

import React from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { Navbar } from "@/components/hx/Navbar";
import { Footer } from "@/components/hx/Footer";
import { ECOSYSTEMS, getEcosystemBySlug } from "@/data/hannonexus";
import { useHannoContent } from "@/data/hanno-content";

const NEON = "#0A9B5F";

export default function EcosystemPage({ slug }) {
  const c = useHannoContent();
  const eco = getEcosystemBySlug(slug);

  if (!eco) return <Navigate to="/" replace />;
  const t = c.eco[eco.slug];

  const others = ECOSYSTEMS.filter((e) => e.slug !== slug);

  return (
    <div className="App relative bg-hx-bg text-hx-text" data-testid={`page-eco-${slug}`}>
      <Navbar />
      <main>
        {/* Cinematic hero with image background */}
        <section className="relative pt-28 pb-12 lg:pt-36 lg:pb-16 overflow-hidden min-h-[60vh] flex items-end">
          <div className="absolute inset-0 pointer-events-none">
            <img
              src={eco.image}
              alt={t.name}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: "brightness(1.18) contrast(1.06) saturate(1.05)" }}
            />
            {/* Soft vignette — only the bottom & left get dark, image stays vibrant on top-right */}
            <div className="absolute inset-0" style={{
              background: "linear-gradient(180deg, rgba(10,15,26,0.15) 0%, rgba(10,15,26,0.05) 30%, rgba(10,15,26,0.55) 78%, rgba(10,15,26,0.92) 100%)",
            }} />
            <div className="absolute inset-0" style={{
              background: "linear-gradient(90deg, rgba(10,15,26,0.55) 0%, rgba(10,15,26,0.15) 45%, rgba(10,15,26,0) 100%)",
            }} />
            <div className="absolute inset-0 hx-grid-bg opacity-[0.05]" />
          </div>

          <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
            <nav className="mb-6 text-[10px] uppercase tracking-[0.22em] text-hx-muted font-mono" data-testid="page-breadcrumb">
              <Link to="/" className="hover:text-hx-neon transition-colors">HannoNexus</Link>
              <span className="mx-2 text-hx-muted/40">/</span>
              <span className="text-hx-text">{t.short}</span>
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-semibold"
            >
              HX Ecosystems · {t.tagline}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-4 font-display font-semibold text-balance text-4xl sm:text-5xl lg:text-[68px] tracking-tight leading-[1.03]"
              style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}
              data-testid="page-title"
            >
              {t.name}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-3xl text-[15px] lg:text-base text-hx-text/85 leading-relaxed"
              style={{ textShadow: "0 1px 2px rgba(10,15,26,0.05)" }}
            >
              {t.description}
            </motion.p>
          </div>
        </section>

        {/* Sub-products grid */}
        <section className="relative py-14 lg:py-20" data-testid="eco-subs">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="max-w-3xl mb-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.eco_page.portfolio_eyebrow}</div>
              <h2 className="mt-3 font-display font-semibold text-3xl lg:text-[44px] tracking-tight leading-[1.05]">
                {c.eco_page.portfolio_title} <span className="text-hx-neon">{t.short}.</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
              {eco.subs.map((s, i) => {
                const desc = t.subs[s.code] || s.desc;
                return (
                <motion.div
                  key={s.code}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  whileHover={{ y: -4 }}
                  className="group relative rounded-2xl border p-6 lg:p-7 overflow-hidden transition-all"
                  style={{
                    borderColor: `${NEON}1f`,
                    background: "linear-gradient(180deg, rgba(22,32,51,0.55) 0%, rgba(10,15,26,0.85) 100%)",
                  }}
                  data-testid={`sub-${s.code.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-5 transition-all group-hover:scale-110"
                       style={{
                         background: `linear-gradient(135deg, ${NEON}22 0%, transparent 70%)`,
                         border: `1px solid ${NEON}55`,
                       }}>
                    <s.icon size={18} strokeWidth={1.8} style={{ color: NEON }} />
                  </div>
                  <h3 className="font-display font-semibold text-xl tracking-tight text-hx-text">{s.code}</h3>
                  <p className="mt-2.5 text-[13px] text-hx-muted leading-relaxed">{desc}</p>
                </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Private-label vehicle lineup — Only rendered for ecosystems with vehicles (e.g., HX Automotive) */}
        {eco.vehicles && eco.vehicles.length > 0 && (
          <section className="relative py-14 lg:py-20 border-t border-white/[0.10] bg-gradient-to-b from-black/40 to-transparent" data-testid="eco-vehicles">
            <div className="max-w-7xl mx-auto px-5 lg:px-8">
              <div className="max-w-3xl mb-10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold">{c.eco_page.lineup_eyebrow}</div>
                <h2 className="mt-3 font-display font-semibold text-3xl lg:text-[44px] tracking-tight leading-[1.05]">
                  {c.eco_page.lineup_title} <span className="text-hx-neon">{c.eco_page.lineup_accent}</span>
                </h2>
                <p className="mt-4 text-[14px] text-hx-muted leading-relaxed max-w-2xl">
                  {c.eco_page.lineup_lede}
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
                {eco.vehicles.map((v, i) => {
                  const vt = t.vehicles?.[v.name] || {};
                  return (
                  <motion.article
                    key={v.name}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.06 }}
                    whileHover={{ y: -6 }}
                    className="group relative rounded-2xl border overflow-hidden transition-all"
                    style={{
                      borderColor: `${NEON}22`,
                      background: "linear-gradient(180deg, rgba(22,32,51,0.65) 0%, rgba(10,15,26,0.85) 100%)",
                    }}
                    data-testid={`vehicle-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {/* Image top */}
                    {v.image && (
                      <div className="relative aspect-[16/10] overflow-hidden">
                        <img
                          src={v.image}
                          alt={v.name}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                          style={{ filter: "brightness(1.15) contrast(1.05) saturate(1.08)" }}
                        />
                        {/* Elegant bottom-only gradient — leaves top ~60% of the image fully bright */}
                        <div className="absolute inset-0" style={{
                          background: "linear-gradient(180deg, rgba(10,15,26,0) 0%, rgba(10,15,26,0) 55%, rgba(10,15,26,0.55) 100%)",
                        }} />
                        {/* Segment badge overlay */}
                        <div className="absolute top-4 left-4 px-2.5 py-1 rounded-md backdrop-blur-xl"
                             style={{ background: "rgba(10,15,26,0.72)", border: `1px solid ${NEON}55` }}>
                          <span className="text-[9px] uppercase tracking-[0.24em] font-mono font-semibold" style={{ color: NEON }}>
                            {vt.segment || v.segment}
                          </span>
                        </div>
                        <div className="absolute top-4 right-4 text-[9px] uppercase tracking-[0.2em] font-mono text-hx-text/85 px-2 py-1 rounded-md backdrop-blur-xl"
                             style={{ background: "rgba(10,15,26,0.72)" }}>
                          {c.eco_page.model} {String(i + 1).padStart(2, "0")}
                        </div>
                      </div>
                    )}

                    <div className="p-6 lg:p-7">
                      {/* Fallback segment/model row when no image */}
                      {!v.image && (
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-[9px] uppercase tracking-[0.28em] font-mono font-semibold" style={{ color: NEON }}>
                            {vt.segment || v.segment}
                          </div>
                          <div className="text-[9px] uppercase tracking-[0.2em] font-mono text-hx-muted/60">
                            {c.eco_page.model} {String(i + 1).padStart(2, "0")}
                          </div>
                        </div>
                      )}
                      <h3 className="font-display font-semibold text-2xl lg:text-[28px] tracking-tight text-hx-text leading-tight"
                          style={{ textShadow: `0 0 24px ${NEON}22` }}>
                        {v.name}
                      </h3>
                      <p className="mt-3 text-[13px] text-hx-muted leading-relaxed">{vt.desc || v.desc}</p>
                      <div className="mt-6 pt-4 border-t flex items-center justify-between text-[9px] uppercase tracking-[0.22em] font-mono"
                           style={{ borderColor: `${NEON}1a`, color: `${NEON}bb` }}>
                        <span>{c.eco_page.private_label_electric}</span>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: NEON, boxShadow: `0 0 8px ${NEON}` }} />
                      </div>
                    </div>
                  </motion.article>
                  );
                })}
              </div>
            </div>
          </section>
        )}

        {/* Value strip */}
        <section className="relative py-14 lg:py-20 border-y border-white/[0.10] bg-gradient-to-b from-black/40 to-transparent">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {c.eco_page.values.map((cv) => (
                <div key={cv.k} className="border-l pl-5" style={{ borderColor: `${NEON}55` }}>
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle2 size={14} style={{ color: NEON }} strokeWidth={2} />
                    <div className="font-display font-semibold text-lg text-hx-text">{cv.k}</div>
                  </div>
                  <p className="text-[13px] text-hx-muted leading-relaxed">{cv.v}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related ecosystems */}
        <section className="relative py-14 lg:py-20" data-testid="eco-related">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-hx-neon font-mono font-semibold mb-3">{c.eco_page.others_eyebrow}</div>
            <h3 className="font-display font-semibold text-2xl lg:text-3xl tracking-tight text-hx-text mb-8">
              {c.eco_page.others_title}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {others.map((e) => {
                const et = c.eco[e.slug];
                return (
                <Link
                  key={e.slug}
                  to={`/${e.slug}`}
                  className="group rounded-xl border p-5 transition-all hover:border-hx-neon/50"
                  style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(10,155,95,0.04)" }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <e.icon size={18} style={{ color: NEON }} strokeWidth={1.8} />
                    <ArrowUpRight size={14} className="text-hx-muted group-hover:text-hx-neon transition-colors" />
                  </div>
                  <div className="font-display font-semibold text-base text-hx-text">{et.short}</div>
                  <div className="mt-1 text-[11px] text-hx-muted line-clamp-2">{et.tagline}</div>
                </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-16 lg:py-20 border-t border-white/[0.10]">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
            <h3 className="font-display font-semibold text-3xl lg:text-4xl tracking-tight leading-[1.1]">
              {c.eco_page.cta_title} <span className="text-hx-neon">{t.short}</span>?
            </h3>
            <p className="mt-4 text-[14px] text-hx-muted leading-relaxed max-w-2xl mx-auto">
              {c.eco_page.cta_lede}
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-hx-neon text-hx-bg font-semibold text-[11px] uppercase tracking-[0.18em] transition-all hover:translate-y-[-2px]"
              style={{ boxShadow: `0 0 24px -4px ${NEON}` }}
            >
              {c.eco_page.cta_button} <ArrowRight size={13} strokeWidth={2.4} />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
